// Today's pure rules (Marketing Hub row 2.3): who may look at which desk,
// which filters and sorts exist, and the plain words printed on the page.
//
// CONSUMER: shared (the Today route, the Today page, tests).
//
// No database and no React here, so every rule is testable in the node pool
// and the server and the page can never disagree about it.

import {
  DECISION_ACTIONS,
  MOMENT_KINDS,
  URGENCIES,
  type DecisionAction,
  type DecisionVerdict,
  type MomentKind,
  type Urgency,
} from "./decision-vocabulary";
import { percent } from "./economics";
import type { WaitingItem } from "./inbox";
import type { ThankYouList } from "./thank-you";
import type { AdvisorMoney, DeskGauge, EffortLine, HeldWhy, MoneyTotals } from "./today-money";

/** The five client-facing companies, the only desks a Today can be scoped to. */
export const TODAY_COMPANY_SLUGS = ["mortgage", "insurance", "planning", "realty", "financial"] as const;
export type TodayCompanySlug = (typeof TODAY_COMPANY_SLUGS)[number];

const COMPANY_WORD: Record<TodayCompanySlug, string> = {
  mortgage: "Mortgage",
  insurance: "Insurance",
  planning: "Planning",
  realty: "Realty",
  financial: "Financial",
};

/** "Insurance", "Financial": the short company word the page prints. */
export function companyWord(slug: string): string {
  return isTodayCompanySlug(slug) ? COMPANY_WORD[slug] : slug;
}

export function isTodayCompanySlug(s: string): s is TodayCompanySlug {
  return (TODAY_COMPANY_SLUGS as readonly string[]).includes(s);
}

// ─── Scope ────────────────────────────────────────────────────────────

export type TodayScopeResult =
  | {
      ok: true;
      /** null means every company (a global role, or an unscoped account). */
      companies: TodayCompanySlug[] | null;
      label: string;
    }
  | {
      ok: false;
      /** What this viewer may look at, for the page to fall back to. */
      allowed: TodayCompanySlug[];
      label: string;
    };

/** "Insurance", "Financial and Planning", "All companies". */
export function scopeLabel(companies: readonly TodayCompanySlug[] | null): string {
  if (companies === null || companies.length === 0) return "All companies";
  const words = TODAY_COMPANY_SLUGS.filter((s) => companies.includes(s)).map((s) => COMPANY_WORD[s]);
  if (words.length === 1) return words[0]!;
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * Decide which companies a Today request may read. `editable` is the
 * session's `editable_company_slugs` ("all" for a global role or an
 * unscoped account); `requested` is the `company` query parameter, or null
 * when the page sent none.
 *
 * No parameter means the viewer's own desk. A parameter naming a company
 * outside the viewer's scope is refused, not trimmed: the route answers 403
 * and the page falls back to the `allowed` list and prints THAT scope in its
 * header ("the header never lies", CLAUDE.md). An unknown slug is ignored
 * the way every list endpoint ignores one.
 */
export function resolveTodayScope(
  editable: readonly string[] | "all",
  requested: readonly string[] | null,
): TodayScopeResult {
  const own: TodayCompanySlug[] | null = editable === "all" ? null : TODAY_COMPANY_SLUGS.filter((s) => editable.includes(s));
  const known = (requested ?? []).filter(isTodayCompanySlug);
  if (known.length === 0) {
    return { ok: true, companies: own, label: scopeLabel(own) };
  }
  if (own !== null) {
    const outside = known.filter((s) => !own.includes(s));
    if (outside.length > 0) {
      return { ok: false, allowed: own, label: scopeLabel(own) };
    }
  }
  const chosen = TODAY_COMPANY_SLUGS.filter((s) => known.includes(s));
  // Every company named is the same as no filter at all: say "All companies".
  const everything = chosen.length === TODAY_COMPANY_SLUGS.length;
  const companies = everything ? null : chosen;
  return { ok: true, companies, label: scopeLabel(companies) };
}

// ─── Filters and sorts ────────────────────────────────────────────────

export const TODAY_BANDS = ["high", "medium", "low", "none"] as const;
export type TodayBand = (typeof TODAY_BANDS)[number];

/** open: nobody has acted or given a verdict yet. handled: someone has. */
export const TODAY_STATUSES = ["open", "handled", "all"] as const;
export type TodayStatus = (typeof TODAY_STATUSES)[number];

export const TODAY_SORTS = ["default", "chance", "urgency", "goes_away", "family"] as const;
export type TodaySort = (typeof TODAY_SORTS)[number];

export const GOES_AWAY_CHOICES = [30, 90, 180] as const;

export interface TodayFilters {
  /** A Services Check key: the product the family needs. */
  products: string[];
  bands: TodayBand[];
  urgencies: Urgency[];
  moments: MomentKind[];
  /** Narrows within the viewer's scope; never widens it. */
  companies: TodayCompanySlug[];
  advisorUserId: string | null;
  goesAwayWithinDays: number | null;
  status: TodayStatus;
}

export const EMPTY_TODAY_FILTERS: TodayFilters = {
  products: [],
  bands: [],
  urgencies: [],
  moments: [],
  companies: [],
  advisorUserId: null,
  goesAwayWithinDays: null,
  status: "open",
};

/** The most values any one list filter may carry, so a query string can
 *  never bind more placeholders than the list's own vocabulary holds. */
const MAX_FILTER_VALUES = 40;

function csv(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "")
    .slice(0, MAX_FILTER_VALUES);
}

function pick<T extends string>(raw: string | undefined, allowed: readonly T[]): T[] {
  const set = new Set<string>(allowed);
  return [...new Set(csv(raw))].filter((v): v is T => set.has(v));
}

/**
 * Filters from a query string, every value checked against a fixed
 * vocabulary. Anything else is dropped, never passed on to SQL. `products`
 * are checked against `knownProducts` (the Services Check catalog, which
 * the caller owns so this file stays free of it).
 */
export function parseTodayFilters(
  query: Readonly<Record<string, string | undefined>>,
  knownProducts: ReadonlySet<string>,
): TodayFilters {
  const goes = Number(query.goes_away_days);
  const status = pick(query.status, TODAY_STATUSES)[0] ?? "open";
  return {
    products: [...new Set(csv(query.product))].filter((p) => knownProducts.has(p)),
    bands: pick(query.band, TODAY_BANDS),
    urgencies: pick(query.urgency, URGENCIES),
    moments: pick(query.moment, MOMENT_KINDS),
    companies: pick(query.filter_company, TODAY_COMPANY_SLUGS),
    advisorUserId: query.advisor && /^[A-Za-z0-9_-]{1,64}$/.test(query.advisor) ? query.advisor : null,
    goesAwayWithinDays: (GOES_AWAY_CHOICES as readonly number[]).includes(goes) ? goes : null,
    status,
  };
}

export function parseTodaySort(raw: string | undefined): { sort: TodaySort; descending: boolean } {
  const sort = pick(raw, TODAY_SORTS)[0] ?? "default";
  return { sort, descending: false };
}

// ─── Words ────────────────────────────────────────────────────────────

const ACTION_LABEL: Record<DecisionAction, string> = {
  do_nothing: "Nothing to do",
  fix_or_answer: "Fix it or answer them",
  ask_one_question: "Ask the one question",
  send_education: "Send something useful",
  draft_personal_message: "Read the draft",
  invite_to_review: "Invite them to a review",
  ask_review_or_introduction: "Ask for a review or an introduction",
  begin_product_conversation: "Start the conversation",
  route_to_company: "Tell the other company",
  suppress_marketing: "Leave them alone",
};

/**
 * The one thing the OS suggests for a row, in the words the drawing uses
 * ("Ask the one question", "Tell Ryan"). `otherCompany` names the company
 * a route_to_company row hands the family to, when the row knows it.
 */
export function suggestedStep(action: DecisionAction, otherCompany?: string | null): string {
  if (action === "route_to_company" && otherCompany) return `Tell ${otherCompany}`;
  return ACTION_LABEL[action];
}

export function isDecisionAction(s: string): s is DecisionAction {
  return (DECISION_ACTIONS as readonly string[]).includes(s);
}

export const URGENCY_LABEL: Record<Urgency, string> = {
  waiting: "Waiting on us",
  this_week: "This week",
  this_month: "This month",
  this_quarter: "This quarter",
  no_rush: "No rush",
};

/** "High · 25%", "Low · 2.1%" (band, percent and the word that says where
 *  it came from print together, never a bare number: build spec, screen 1).
 *  The percent through the brief's own formatter, so the chip and "Chance
 *  it closes" in the brief print the same characters. */
export function chanceChip(band: string | null, pct: number | null): string {
  if (band === null || pct === null) return "No estimate";
  const word = band === "high" ? "High" : band === "medium" ? "Medium" : "Low";
  return `${word} · ${percent(pct / 100)}`;
}

/** The cache_key prefix of every saved "households read" count. A merge, an
 *  unmerge, an archive or a restore deletes them all (the household set just
 *  changed), so a family that left never waits out the fifteen minutes. */
export const TODAY_READ_SNAPSHOT_PREFIX = "marketing-today-read:";

export interface TodayCounts {
  /** Households the pass read that belong on this desk. */
  read: number;
  /** Of those, how many have a moment for this desk. */
  withMoment: number;
  /** The rest: nothing due. */
  nothingDue: number;
  /** Moments nobody has acted on yet. */
  open: number;
  /** Open moments with a High chance. */
  highOpen: number;
  /** When `read` (and so `nothingDue`) was counted, if it came from the
   *  saved count; null when it was counted just now. The page prints it, so
   *  a saved number is never shown as fresher than it is. */
  readAsOf?: string | null;
}

/** "1,940 households read, 23 with a moment, 1,917 with nothing due". */
export function scopeLine(counts: TodayCounts): string {
  const n = (x: number) => x.toLocaleString("en-US");
  return `${n(counts.read)} household${counts.read === 1 ? "" : "s"} read, ${momentCountWords(counts)}, ${n(counts.nothingDue)} with nothing due`;
}

/**
 * "45 with a moment (41 still open)". ONE sentence for the two numbers every
 * Today screen prints, so the header, the tile, the morning summary and the
 * table all name the same population by the same words (audit S1-H1,
 * 2026-10-27: the page printed 45, 44, 41 and 40 for what looked like one
 * thing, because "with a moment" and "still open" were never told apart).
 * Both come from one query (countMoments); `open` is the part nobody has
 * acted on yet. When nothing has been handled, the second number is left out
 * rather than repeated.
 */
export function momentCountWords(counts: Pick<TodayCounts, "withMoment" | "open">): string {
  const n = (x: number) => x.toLocaleString("en-US");
  const base = `${n(counts.withMoment)} with a moment`;
  return counts.open === counts.withMoment ? base : `${base} (${n(counts.open)} still open)`;
}

/**
 * The table's heading. The number is the table's own total, so the words say
 * which slice of the moments it counts: the Show filter decides that, and a
 * heading that always said "with a moment" over a "still open" list was one
 * of the four disagreeing numbers.
 */
export function tableHeading(status: "open" | "handled" | "all", total: number): string {
  const n = total.toLocaleString("en-US");
  if (status === "open") return `Still open · ${n}`;
  if (status === "handled") return `Already handled · ${n}`;
  return `Every family with a moment · ${n}`;
}

/**
 * Reads never trust two subtractions to agree: the three counts come from
 * one query, and this only derives the last from the other two. A negative
 * result means the query disagreed with itself, which must surface.
 */
export function countsFrom(read: number, withMoment: number, open: number, highOpen: number): TodayCounts {
  const nothingDue = read - withMoment;
  if (nothingDue < 0 || open > withMoment || highOpen > open) {
    throw new Error(`Today's counts disagree: read ${read}, with a moment ${withMoment}, open ${open}, high ${highOpen}`);
  }
  return { read, withMoment, nothingDue, open, highOpen, readAsOf: null };
}

// ─── What the route answers (one type for the server and the page) ─────

export interface TodayRow {
  decision_id: string;
  household_id: string;
  household_name: string;
  company_slug: string | null;
  company_name: string | null;
  moment_kind: MomentKind | null;
  moment_word: string;
  product_family: string | null;
  product_label: string | null;
  action: DecisionAction;
  next_step: string;
  tier: string | null;
  /** The reason the OS gives, with where it came from and the date it is as of. */
  moment_text: string;
  moment_source: string | null;
  moment_as_of: string | null;
  chance_pct: number | null;
  chance_band: string | null;
  chance_source: string | null;
  urgency: Urgency | null;
  urgency_label: string | null;
  expires_on: string | null;
  advisor_user_id: string | null;
  advisor_name: string | null;
  /** The last conversation FOR THIS COMPANY, or null when none is on record. */
  last_conversation: { at: string; kind: string } | null;
  verdict: DecisionVerdict | null;
  outcome: string | null;
  decided_on: string;
  /** The household's primary contact's number, for the Call button's phone
   *  link. Null when none is on file (the button then says so). Absent from
   *  the export. */
  call_phone: string | null;
  /** For a route row, the company the family is handed to ("Insurance"). */
  route_target: { slug: string; name: string } | null;
  /** Whether Snooze can write this row to the service map (it names a product). */
  can_snooze: boolean;
  /** Whether "Write the email" is offered (the suggested step is a message). */
  offers_email: boolean;
  /** What "Write the email" left: a draft in Outlook (with its link) or a
   *  sent email. Null when nobody has written one. Absent from the export. */
  email: { mode: "draft" | "sent"; web_link: string | null } | null;
  /** Row 2.7c: this row's next step uses a conversation slot and its desk has
   *  none this week (or none set). A NOTE only: the row stays, every button
   *  works, and nothing is hidden. Absent from the export. */
  held?: HeldWhy | null;
  /** Row 10.6: a purchased (restricted) signal helped order this row (a
   *  tie-break after the chance, never the reason). Drawn as the lock chip
   *  with STATUS_CHIP.restricted's words. Absent from the export. */
  ordered_by_restricted?: boolean;
  /** Row 11.4: the "soon" goal this family is on for this company, when one is prepared or approved. */
  goal?: { id: string; sentence: string } | null;
}

export interface TodayFilterOptions {
  products: { key: string; label: string }[];
  advisors: { id: string; name: string }[];
}

export interface TodayResponse {
  /** The viewer's own open hand-raises (row 2.4), oldest deadline first,
   *  with the total from its own COUNT(*). Empty in the export. */
  waiting: { items: WaitingItem[]; total: number };
  /** false before any pass has finished: nothing honest to show yet. */
  computed: boolean;
  as_of: { run_date: string; finished_at: string; is_today: boolean } | null;
  today: string;
  /** The scope the server actually applied; the header prints THIS. */
  scope: { companies: TodayCompanySlug[] | null; label: string; advisor_user_id: string | null };
  counts: TodayCounts;
  tile: TodayRow[];
  rows: TodayRow[];
  /** COUNT(*) over the same WHERE as `rows`. */
  total: number;
  offset: number;
  limit: number;
  filter_options: TodayFilterOptions;
  /** A household with nothing due and moments held, for "why each". */
  why_each_household_id: string | null;
  /** Rule 7b (row 2.5): the families on this desk a person or their own
   *  words paused, and the firm-wide line. Null in the export. */
  fatigue: TodayFatigue | null;
  /** Row 2.6: "desk" is screen 1; "firm" is Grant's and Baker's screen 2,
   *  and `firm` then carries it. */
  view: TodayView;
  /** Whether this viewer gets the firm view (so a desk view can offer the
   *  way back to it). */
  can_see_firm: boolean;
  /** The viewer's own advisor thank-yous (row 2.6), oldest first. Null in
   *  the export and on the firm view (which carries the owner's). */
  thank_yous: ThankYouList | null;
  firm: FirmToday | null;
  /** Row 2.7c: what acting on this desk's open families should earn, from
   *  the same open moments as `counts.open`. Null in the export, on the
   *  firm view (which carries its own) and before any pass has finished. */
  money: MoneyTotals | null;
  /** Row 2.7c: this week's slots on each desk in scope, one gauge each. */
  desks: DeskGauge[];
  /** Row 3.0: Outlook drafts this viewer's own Today presses made that have
   *  not been sent (countDraftsWaiting). The viewer's own, whatever the desk.
   *  Null in the export and on the firm view, which does not show the card. */
  drafts_waiting: number | null;
  /** Row 7.9a: the rows waiting on the viewer's own signature (Jake, Ryan)
   *  and the viewer's own series and goals Grant sent back, with his note.
   *  Null in the export and on the firm view. */
  approvals_for_me: ApprovalsForMe | null;
}

export interface ApprovalsForMe {
  to_sign: number;
  sent_back: { kind: string; id: string; name: string; note: string | null; at: string; href: string }[];
}

// ─── The firm view (row 2.6, drawing screen 2) ────────────────────────

export const TODAY_VIEWS = ["desk", "firm"] as const;
export type TodayView = (typeof TODAY_VIEWS)[number];

/**
 * Who gets the firm view: Grant (system_admin) and Baker, the cross-company
 * roles that keep firm-wide defaults by design (CLAUDE.md, "default to your
 * desk"). A compliance seat scoped to one company (Cambria, Mortgage) is a
 * desk like any other. Everyone else is served screen 1, and the server
 * never builds the firm view for them.
 */
export function seesFirmToday(roleSlug: string | null, companyScope: readonly string[] | null): boolean {
  if (roleSlug === "system_admin") return true;
  return roleSlug === "compliance" && (companyScope === null || companyScope.length === 0);
}

/**
 * Which view a request gets. A firm viewer asking for `view=desk` gets
 * screen 1 (the page sends it when Grant opens one advisor's line or the
 * whole firm's table); nobody else can ask for the firm. A saved "My book"
 * lens alone never moves a firm viewer off the firm view.
 */
export function resolveTodayView(firmViewer: boolean, requested: string | undefined): TodayView {
  if (!firmViewer) return "desk";
  return requested === "desk" ? "desk" : "firm";
}

/** One advisor's line on the firm view: counts, and (row 2.7c) the money
 *  their open families should earn, never an hour. */
export interface AdvisorToday {
  /** Null: moments with no advisor on file, one line of their own. */
  advisor_user_id: string | null;
  advisor_name: string | null;
  /** "Insurance", "Financial": the companies their moments are for. */
  companies: string[];
  with_moment: number;
  open: number;
  high_open: number;
  /** Row 2.7c. Always present; `counted` 0 says why instead of a zero. */
  money: AdvisorMoney;
}

export interface FirmToday {
  /** Every company's header counts (screen 1's, unscoped). Null before any
   *  pass has finished. */
  counts: TodayCounts | null;
  /** The owner's own thank-yous: a card in Grant's hand. */
  thank_yous: ThankYouList;
  /** Program audit M6: whether the viewer is Grant (by user id), the one person who marks these cards. */
  thank_yous_yours: boolean;
  advisors: AdvisorToday[];
  /** Row 2.7c: the firm's open families priced (the strip's numbers, whole
   *  firm), and one line per effort for "This quarter's efforts". */
  money: MoneyTotals;
  efforts: EffortLine[];
  /** Row 2.7c: every desk's week, for the held words under the chart. */
  desks: DeskGauge[];
  /** "Waiting on you": what exists today, and plain words for what does not.
   *  The approval queue's rows (row 7.9a), counted from the same read the
   *  queue page uses; the studio's Friday suggestions are a later slice, so
   *  their count is null, never a zero nobody measured. */
  /** Row 7.9b: `not_sending` counts approved series pieces that missed their window or are held back. */
  waiting: {
    approvals: { total: number; high: number } | null;
    not_sending: number | null;
    studio_suggestions: null;
    fatigue: FirmFatigueState;
    platform_rules: number | null;
    /** Row 9a.2: companies (and the firm ceiling) at or past their alert line on MEASURED spend this month; never a reservation. */
    budget_alerts: number | null;
    /** Row 9a.4: ad accounts whose provider figure and the OS's confirmed sum differed by more than $50 at the last nightly pull. */
    spend_mismatches: number | null;
    /** Row 10.7: running paid rows the nightly spend-tier read calls saturating. */
    saturating: number | null;
  };
}

/** "Brandon · Insurance", "No advisor on file". */
export function advisorLineName(a: Pick<AdvisorToday, "advisor_name" | "companies" | "advisor_user_id">): string {
  if (a.advisor_user_id === null) return "No advisor on file";
  const name = a.advisor_name ?? "An advisor";
  return a.companies.length > 0 ? `${name} · ${a.companies.join(", ")}` : name;
}

/** "23 with a moment · 4 High". */
export function advisorLineCounts(a: Pick<AdvisorToday, "with_moment" | "high_open">): string {
  return `${a.with_moment.toLocaleString("en-US")} with a moment · ${a.high_open.toLocaleString("en-US")} High`;
}

const EMPTY_ADVISOR_MONEY: AdvisorMoney = { advisor_user_id: null, income_y1: 0, income_lifetime: 0, held: 0, without_estimate: 0, counted: 0 };

/** An advisor line's money, or the empty one when none of their families
 *  is open (their moments are all handled). */
export function moneyForAdvisor(all: readonly AdvisorMoney[], advisorUserId: string | null): AdvisorMoney {
  return all.find((m) => m.advisor_user_id === advisorUserId) ?? { ...EMPTY_ADVISOR_MONEY, advisor_user_id: advisorUserId };
}

/** One family paused by a person or by their own words (row 2.5). */
export interface QuietFamily {
  pause_id: string;
  household_id: string;
  household_name: string;
  kind: "do_not_market" | "not_interested" | "asked_to_stop" | "complained";
  started_at: string;
  /** Null: until a person lifts it. */
  ends_at: string | null;
}

export interface FirmFatigueState {
  verdict: "clear" | "on_grants_today" | "stop";
  sentence: string;
  complaints_last_30_days: number;
  /** One-click unsubscribes in the last 30 days, all of Stewardship. */
  unsubscribes_last_30_days: number;
  /** "Not now" answers told in the last 30 days, all of Stewardship. */
  not_now_last_30_days: number;
  /** The last Google Postmaster read reached Google (row 6.2). The rate is
   *  never guessed: null when Google published no figure in three days. */
  postmaster_connected: boolean;
  postmaster_spam_rate?: number | null;
  postmaster_rate_date?: string | null;
}

export interface TodayFatigue {
  quiet: { items: QuietFamily[]; total: number };
  firm: FirmFatigueState;
}

/** What each stored pause says on Today. */
export const QUIET_WORD: Record<QuietFamily["kind"], string> = {
  do_not_market: "Marked do not market",
  not_interested: "Said not interested",
  asked_to_stop: "Asked to stop hearing from us",
  complained: "Marked our email as spam",
};

/** The 403 body when a viewer asks for a desk outside their scope. */
export interface TodayScopeDenied {
  error: "forbidden_company_scope";
  message: string;
  allowed_companies: TodayCompanySlug[];
  scope_label: string;
}

// ─── "Do not market": the reason it carries (audit S1-H3, 2026-10-27) ──
//
// "Do not market" pauses every piece of marketing to a family until someone
// lifts it, so it asks why before it acts (CLAUDE.md, reversibility-first
// rule 4: reason codes are first-class data). The codes are this one
// action's own short list, kept beside the action; the choice and any words
// go to the audit row, never into a client-facing message.

export const DO_NOT_MARKET_REASONS = [
  { code: "client_asked", label: "The family asked us to stop", hint: "They told an advisor, or replied to something, that they do not want marketing." },
  { code: "family_loss_or_sensitive", label: "A loss or something sensitive in the family", hint: "A death, an illness or a hard season, where a marketing note would land badly." },
  { code: "not_a_fit_for_marketing", label: "Marketing does not suit this family", hint: "We still serve them. They are just not someone to send offers to." },
  { code: "record_problem", label: "The record looks wrong", hint: "A duplicate or wrong person. Fix the record, then lift this." },
  { code: "other_with_text", label: "Something else (say what)", hint: "Write the reason in the box." },
] as const;

export type DoNotMarketReasonCode = (typeof DO_NOT_MARKET_REASONS)[number]["code"];

export function isDoNotMarketReasonCode(v: unknown): v is DoNotMarketReasonCode {
  return typeof v === "string" && DO_NOT_MARKET_REASONS.some((r) => r.code === v);
}

/** Why a "Do not market" request cannot go through yet, or null when it can. Server and page share it. */
export function doNotMarketReasonProblem(code: unknown, text: unknown): string | null {
  if (!isDoNotMarketReasonCode(code)) return "Pick why this family should not be marketed to.";
  if (code === "other_with_text" && (typeof text !== "string" || text.trim().length === 0)) return "Say what the reason is.";
  return null;
}
